/**
 * src/components/ledger/SplitBillModal.tsx
 * Split Expenses Calculator allowing equal or custom division of bills across team members
 * with automatic penny-rounding conservation and batch request generation.
 */

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Split, 
  Check, 
  Send, 
  AlertCircle, 
  DollarSign, 
  UserCheck, 
  Calculator,
  ShieldCheck
} from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { api, type UserItem, type CategoryItem } from '../../services/api';

interface SplitBillModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserItem[];
  categories: CategoryItem[];
  onComplete?: () => void;
}

export const SplitBillModal: React.FC<SplitBillModalProps> = ({
  isOpen,
  onClose,
  users,
  categories,
  onComplete
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';

  const [totalAmount, setTotalAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('Food & Meals');
  const [splitMode, setSplitMode] = useState<'equal' | 'custom'>('equal');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [customShares, setCustomShares] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>('');

  // Payer state (who paid the bill out-of-pocket)
  const [payerId, setPayerId] = useState<number>(() => user?.id || 1);
  const [includePayerInSplit, setIncludePayerInSplit] = useState<boolean>(true);

  // Filter only active users (deactivated users are completely removed)
  const activeUsers = users.filter(u => u.is_active !== 0);

  // Initialize selected users when opened
  useEffect(() => {
    if (isOpen) {
      setTotalAmount('');
      setDescription('');
      setCategoryName(categories[0]?.name || 'Food & Meals');
      setSplitMode('equal');
      setFormError('');
      setCustomShares({});
      setPayerId(user?.id || (activeUsers[0]?.id ?? 1));
      setIncludePayerInSplit(true);
      // Default to selecting all active users
      setSelectedUserIds(activeUsers.map(u => u.id));
    }
  }, [isOpen, users, categories, user]);

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === activeUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(activeUsers.map(u => u.id));
    }
  };

  const parsedTotal = parseFloat(totalAmount) || 0;

  // Determining effective participants based on whether payer is included
  const effectiveParticipantIds = includePayerInSplit
    ? (selectedUserIds.includes(payerId) ? selectedUserIds : [...selectedUserIds, payerId])
    : selectedUserIds.filter(id => id !== payerId);

  const participantCount = effectiveParticipantIds.length;

  // Calculate equal split shares with penny-rounding conservation
  const calculateEqualShares = (): { userId: number; amount: number }[] => {
    if (parsedTotal <= 0 || participantCount === 0) return [];

    const baseShare = Math.floor((parsedTotal * 100) / participantCount) / 100;
    const remainder = Math.round((parsedTotal - baseShare * participantCount) * 100) / 100;

    return effectiveParticipantIds.map((userId, idx) => ({
      userId,
      amount: idx === 0 ? Math.round((baseShare + remainder) * 100) / 100 : baseShare
    }));
  };

  // Custom split balance calculation
  const totalCustomAllocated = effectiveParticipantIds.reduce((sum, id) => {
    const val = parseFloat(customShares[id] || '0') || 0;
    return Math.round((sum + val) * 100) / 100;
  }, 0);
  const customDifference = Math.round((parsedTotal - totalCustomAllocated) * 100) / 100;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (parsedTotal <= 0) {
      setFormError('Please enter a total bill amount greater than 0.');
      return;
    }

    if (!description.trim()) {
      setFormError('Please enter a description or reason for this bill.');
      return;
    }

    if (participantCount === 0) {
      setFormError('Please select at least 1 member to split this bill with.');
      return;
    }

    let allShares: { userId: number; amount: number }[] = [];

    if (splitMode === 'equal') {
      allShares = calculateEqualShares();
    } else {
      if (Math.abs(customDifference) > 0.01) {
        setFormError(`Custom shares total (₹${totalCustomAllocated}) does not match total bill (₹${parsedTotal}). Difference: ₹${customDifference}`);
        return;
      }
      allShares = effectiveParticipantIds.map(userId => ({
        userId,
        amount: parseFloat(customShares[userId] || '0') || 0
      })).filter(s => s.amount > 0);
    }

    // CRITICAL: Payer does NOT repay themselves!
    // Transactions/approval requests are created ONLY for OTHER members.
    // Payer's share is self-covered out-of-pocket.
    const nonPayerShares = allShares.filter(s => s.userId !== payerId);

    if (nonPayerShares.length === 0) {
      setFormError('All shares are allocated to the payer. Please select at least one other participant to split with.');
      return;
    }

    setIsSubmitting(true);
    try {
      let createdCount = 0;
      const payerObj = activeUsers.find(u => u.id === payerId);
      const payerName = payerObj?.username || 'Payer';

      for (const share of nonPayerShares) {
        const itemDesc = `${description.trim()} (Split Bill paid by ${payerName}: ₹${share.amount} of ₹${parsedTotal})`;

        if (isAdmin) {
          // Admin directly commits debit transaction for each other participant
          await api.transactions.create({
            user_id: share.userId,
            transaction_type: 'paid', // Debit from other member
            amount: share.amount,
            category_name: categoryName,
            description: itemDesc,
            transaction_date: new Date().toISOString()
          });
        } else {
          // Regular user creates pending approval requests
          await api.approvals.create({
            user_id: share.userId,
            transaction_type: 'paid', // Debit
            amount: share.amount,
            category_name: categoryName,
            description: itemDesc,
            transaction_date: new Date().toISOString()
          });
        }
        createdCount++;
      }

      const payerShareObj = allShares.find(s => s.userId === payerId);
      const payerShareAmount = payerShareObj ? payerShareObj.amount : 0;

      showToast(
        isAdmin
          ? `Split ₹${parsedTotal} successfully! ₹${payerShareAmount} self-covered by ${payerName}, debited ₹${parsedTotal - payerShareAmount} across ${createdCount} other members.`
          : `Submitted split bill requests for ${createdCount} members for Admin review.`,
        'success'
      );

      if (onComplete) onComplete();
      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Failed to split bill');
    } finally {
      setIsSubmitting(false);
    }
  };

  const equalShares = calculateEqualShares();

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Split Expenses Calculator"
      maxWidth="580px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {formError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f87171',
            fontSize: '0.875rem'
          }}>
            {formError}
          </div>
        )}

        {/* Total Amount & Category */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Total Bill Amount (₹)
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'var(--ios-blue)',
                pointerEvents: 'none'
              }}>
                ₹
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                placeholder="0.00"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="glass-input num-font"
                style={{
                  paddingLeft: '34px',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Category
            </label>
            <select
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              className="glass-select"
              style={{ width: '100%', height: '44px' }}
            >
              {categories.map(c => (
                <option key={c.id} value={c.name}>
                  {c.icon || '🏷️'} {c.name}
                </option>
              ))}
              <option value="Food & Meals">🍔 Food & Meals</option>
            </select>
          </div>
        </div>

        {/* Bill Description */}
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
            Description / Occasion
          </label>
          <input
            type="text"
            required
            placeholder="e.g. Team dinner, Friday snacks, Taxi ride"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="glass-input"
            style={{ fontSize: '0.875rem' }}
          />
        </div>

        {/* Paid By (Payer) & Include Payer Toggle */}
        <div className="ios-inset-group" style={{ padding: '12px 14px', background: 'rgba(118, 118, 128, 0.12)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '10px', alignItems: 'center' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                Paid By (Out of Pocket)
              </label>
              <select
                value={payerId}
                onChange={(e) => setPayerId(parseInt(e.target.value, 10))}
                className="glass-select"
                style={{ width: '100%', height: '38px', fontSize: '0.84rem' }}
              >
                {activeUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.username} {u.id === user?.id ? '(You)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                Payer's Share
              </label>
              <button
                type="button"
                onClick={() => setIncludePayerInSplit(prev => !prev)}
                className="ios-tap-target"
                style={{
                  width: '100%',
                  height: '38px',
                  borderRadius: 'var(--radius-button)',
                  border: includePayerInSplit ? '1px solid var(--ios-blue)' : 'var(--border-hairline)',
                  background: includePayerInSplit ? 'rgba(10, 132, 255, 0.15)' : 'rgba(118, 118, 128, 0.15)',
                  color: includePayerInSplit ? 'var(--ios-blue)' : 'var(--text-secondary)',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <Check size={14} strokeWidth={includePayerInSplit ? 2.5 : 1.5} color={includePayerInSplit ? 'var(--ios-blue)' : 'transparent'} />
                <span>{includePayerInSplit ? 'My Share Included' : 'For Others Only'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Split Mode Toggle - iOS Segmented Capsule */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Split Distribution
            </label>
            <div className="ios-segmented-control" style={{ padding: '2px' }}>
              <button
                type="button"
                onClick={() => setSplitMode('equal')}
                className={`ios-segment-button ${splitMode === 'equal' ? 'active' : ''}`}
                style={{ padding: '4px 12px', fontSize: '0.75rem' }}
              >
                Equal Shares
              </button>
              <button
                type="button"
                onClick={() => setSplitMode('custom')}
                className={`ios-segment-button ${splitMode === 'custom' ? 'active' : ''}`}
                style={{ padding: '4px 12px', fontSize: '0.75rem' }}
              >
                Custom
              </button>
            </div>
          </div>
        </div>

        {/* Participants Selection Checklist */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Selected: <strong>{participantCount} of {activeUsers.length} members</strong>
            </span>
            <button
              type="button"
              onClick={handleSelectAll}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent-cyan)',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {selectedUserIds.length === activeUsers.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(0, 0, 0, 0.2)',
            border: '1px solid var(--border-glass)',
            padding: '0.5rem'
          }}>
            {activeUsers.map(u => {
              const isSelected = selectedUserIds.includes(u.id);
              const equalShare = equalShares.find(s => s.userId === u.id)?.amount;

              return (
                <div
                  key={u.id}
                  onClick={() => toggleUserSelection(u.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius-sm)',
                    background: isSelected ? 'rgba(56, 189, 248, 0.08)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.1s ease',
                    marginBottom: '0.25rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '4px',
                      border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--text-tertiary)',
                      background: isSelected ? 'var(--accent-cyan)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000'
                    }}>
                      {isSelected && <Check size={14} strokeWidth={3} />}
                    </div>

                    <span style={{ fontSize: '0.875rem', fontWeight: isSelected ? 600 : 400, color: 'var(--text-primary)' }}>
                      {u.username}
                    </span>
                  </div>

                  {isSelected && (
                    <div onClick={(e) => e.stopPropagation()}>
                      {splitMode === 'equal' ? (
                        <span style={{
                          fontSize: '0.8125rem',
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-pill)',
                          background: u.id === payerId ? 'rgba(100, 210, 255, 0.16)' : 'rgba(255, 69, 58, 0.12)',
                          color: u.id === payerId ? 'var(--ios-teal)' : 'var(--ios-red)'
                        }}>
                          {u.id === payerId ? `₹${equalShare?.toFixed(2) || '0.00'} (Self-covered)` : `-₹${equalShare?.toFixed(2) || '0.00'}`}
                        </span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>₹</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={customShares[u.id] || ''}
                            onChange={(e) => setCustomShares({ ...customShares, [u.id]: e.target.value })}
                            style={{
                              width: '80px',
                              padding: '0.25rem 0.5rem',
                              borderRadius: 'var(--radius-sm)',
                              background: 'rgba(255, 255, 255, 0.08)',
                              border: '1px solid var(--border-glass)',
                              color: 'var(--text-primary)',
                              fontSize: '0.8125rem',
                              textAlign: 'right',
                              outline: 'none'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Split Discrepancy Note */}
        {splitMode === 'custom' && parsedTotal > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.625rem 0.875rem',
            borderRadius: 'var(--radius-md)',
            background: Math.abs(customDifference) <= 0.01 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${Math.abs(customDifference) <= 0.01 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            fontSize: '0.8125rem'
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              Allocated: <strong>₹{totalCustomAllocated}</strong> of ₹{parsedTotal}
            </span>
            <span style={{
              fontWeight: 700,
              color: Math.abs(customDifference) <= 0.01 ? '#34d399' : '#f87171'
            }}>
              {Math.abs(customDifference) <= 0.01 ? '✓ Balanced' : `Diff: ₹${customDifference}`}
            </span>
          </div>
        )}

        {/* Dynamic Calculation Breakdown */}
        {parsedTotal > 0 && participantCount > 0 && (
          <div
            className="ios-inset-group"
            style={{
              padding: '14px 16px',
              background: 'rgba(10, 132, 255, 0.08)',
              border: '1px solid rgba(10, 132, 255, 0.28)',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}
          >
            <div className="flex-between">
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Total Bill (Paid by <strong>{activeUsers.find(u => u.id === payerId)?.username || 'Payer'}</strong>):
              </span>
              <span className="num-font" style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                ₹{parsedTotal.toFixed(2)}
              </span>
            </div>

            {includePayerInSplit && (
              <div className="flex-between">
                <span style={{ fontSize: '0.8125rem', color: 'var(--ios-teal)' }}>
                  ✓ Payer Share (Self-Covered — No Debt):
                </span>
                <span className="num-font" style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--ios-teal)' }}>
                  ₹{(equalShares.find(s => s.userId === payerId)?.amount || (parsedTotal / Math.max(1, participantCount))).toFixed(2)}
                </span>
              </div>
            )}

            <div className="flex-between" style={{ borderTop: 'var(--border-hairline)', paddingTop: '6px', marginTop: '2px' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--ios-blue)' }}>
                Amount to Collect from {effectiveParticipantIds.filter(id => id !== payerId).length} Other Member(s):
              </span>
              <span className="num-font" style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--ios-blue)' }}>
                ₹{(parsedTotal - (includePayerInSplit ? (equalShares.find(s => s.userId === payerId)?.amount || (parsedTotal / Math.max(1, participantCount))) : 0)).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div style={{
          display: 'flex',
          gap: '0.625rem',
          padding: '0.75rem',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(56, 189, 248, 0.08)',
          border: '1px solid rgba(56, 189, 248, 0.2)',
          color: '#38bdf8',
          fontSize: '0.8125rem',
          lineHeight: 1.4
        }}>
          <Calculator size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            {isAdmin 
              ? 'Each participant will have their share directly debited from their ledger balance.'
              : 'Each share will be queued as an approval request for Admin verification.'}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
          <GlassButton
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </GlassButton>
          <GlassButton type="submit" variant="primary" disabled={isSubmitting}>
            <Split size={16} />
            {isSubmitting ? 'Splitting...' : `Confirm Split (${participantCount} Shares)`}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
